import { useEffect, useState } from 'react';
import { useStore } from '../store';
import { sessionsApi } from '../services/api';
import {
  ChevronRight,
  ChevronLeft,
  Clock,
  Loader2,
  FileText,
} from 'lucide-react';
import SessionList from './SessionList';

export default function SessionSidebar() {
  const {
    isSessionSidebarOpen,
    toggleSessionSidebar,
    sessions,
    setSessions,
    loadSession,
    currentSessionId,
    createNewSession,
    addNotification,
  } = useStore();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isSessionSidebarOpen) {
      loadSessions();
    }
  }, [isSessionSidebarOpen]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await sessionsApi.getAll();
      setSessions(data);
    } catch {
      addNotification('Failed to load sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSession = (sessionId: string) => {
    loadSession(sessionId);
    toggleSessionSidebar();
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await sessionsApi.delete(sessionId);
      setSessions(sessions.filter((s) => s.id !== sessionId));
      addNotification('Document deleted', 'success');
    } catch {
      addNotification('Failed to delete', 'error');
    }
  };

  const handleNewSession = () => {
    createNewSession();
    toggleSessionSidebar();
  };

  // Collapsed state - narrow bar on the left
  if (!isSessionSidebarOpen) {
    return (
      <div className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-14 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-1 z-30">
        <button
          onClick={toggleSessionSidebar}
          className="p-2 hover:bg-gray-100 rounded-lg mb-2 transition-colors"
          title="Expand history"
        >
          <ChevronRight size={18} className="text-gray-600" />
        </button>

        <div className="w-8 border-t border-gray-200 mb-2" />

        <button
          onClick={toggleSessionSidebar}
          className="p-2 rounded-lg transition-colors text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="History"
        >
          <Clock size={18} />
        </button>

        <button
          onClick={() => {
            createNewSession();
          }}
          className="p-2 rounded-lg transition-colors text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="New Document"
        >
          <FileText size={18} />
        </button>
      </div>
    );
  }

  // Expanded state - full panel
  return (
    <div className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-80 bg-white border-r border-gray-200 shadow-lg z-30 flex flex-col overflow-hidden">
      {/* Header with collapse button */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50/80">
        <div className="flex items-center gap-1.5">
          <Clock size={15} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Document History</span>
        </div>
        <button
          onClick={toggleSessionSidebar}
          className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          title="Collapse sidebar"
        >
          <ChevronLeft size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <SessionList
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelectSession={handleSelectSession}
            onDeleteSession={handleDeleteSession}
            onNewSession={handleNewSession}
          />
        )}
      </div>
    </div>
  );
}
